/**
 *
 * Mixin que trata da criação de gráficos para as páginas de reporting
 *
*/

const getStatisticsAndLabels = ( statisticType, apiData ) => {
    return statisticType( apiData );
};

const addNewPieChart = ({
    statisticType,
    updatedData,
    chartOptions
}) => {
    const chartData = getStatisticsAndLabels( statisticType, updatedData );

    if ( chartData.series.length ) {
        return {
            series: chartData.series,
            chartOptions: {
                chart: {
                    width: '100%',
                    type: 'pie',
                    toolbar: {
                        show: false
                    }
                },
                /* eslint-disable max-len */
                colors: [ '#107E7D', '#044B7F', '#A41623', '#F85E00', '#7180AC', '#2B4570', '#A37A74', '#210B2C', '#55286F', '#AE759F', '#808F87', '#9B7E46', '#52154E', '#111344', '#9A4C95', '#4D2D52', '#1D1A31', '#CC5803', '#1F1300', '#275DAD', '#5B616A', '#5C6F68', '#8AA39B', '#C880B7', '#9F6BA0', '#4A2040', '#433E0E', '#7C9082', '#776885', '#5F1A37', '#04030F', '#DE6B48', '#EC0B43', '#58355E', '#9E6240', '#CD4631', '#95190C', '#610345', '#C97C5D', '#B36A5E', '#918450', '#2A2D34', '#009DDC', '#F26430', '#6761A8', '#009B72', '#253031', '#315659', '#2978A0', '#8E7DBE' ],
                labels: chartData.labels,
                legend: {
                    markers: {
                        width: 12,
                        height: 12,
                        radius: 8
                    },
                    position: chartOptions.legendPosition || 'right',
                    height: chartOptions.legendHeight || undefined,
                    horizontalAlign: chartOptions.legendHorizontalAlign || 'center',
                    fontSize: '12px'
                },
                theme: {
                    monochrome: {
                        enabled: false,
                        color: '#4D7AEB'
                    }
                },
                title: {
                    text: chartOptions.title,
                    margin: 10,
                    style: {
                        fontSize: chartOptions.titleSize || '25px',
                        fontWeight: 'bold',
                        color: '#707070'
                    }
                },
                responsive: [
                    {
                        breakpoint: 1400,
                        options: {
                            chart: {
                                width: '90%',
                                height: 500
                            }
                        }
                    }, {
                        breakpoint: 800,
                        options: {
                            chart: {
                                height: 400
                            },
                            legend: {
                                position: 'bottom',
                                horizontalAlign: 'left'
                            }
                        }
                    }, {
                        breakpoint: 480,
                        options: {
                            title: {
                                text: chartOptions.title,
                                margin: 10,
                                style: {
                                    fontSize: '17px',
                                    fontWeight: 'bold',
                                    color: '#707070'
                                }
                            }
                        }
                    }, {
                        breakpoint: 360,
                        options: {
                            title: {
                                text: chartOptions.title,
                                margin: 10,
                                style: {
                                    fontSize: '15px',
                                    fontWeight: 'bold',
                                    color: '#707070'
                                }
                            }
                        }
                    }
                ]
            }
        };
    }

    return { hasNoData: true };
};

const addNewBarChart = ({
    statisticType,
    updatedData,
    chartOptions
}) => {
    const chartData = getStatisticsAndLabels( statisticType, updatedData );

    if ( chartData.series.some( serie => serie.data.some( data => data !== 0 ) ) ) {
        return {
            series: chartData.series,
            chartOptions: {
                chart: {
                    type: 'bar',
                    height: chartOptions.chartHeight || 350,
                    stacked: chartOptions.stacked || false,
                    zoom: {
                        enabled: true
                    },
                    toolbar: {
                        show: true,
                        export: {
                            csv: {
                                columnDelimiter: ';'
                            }
                        }
                    }
                },
                grid: {
                    show: chartOptions.showGrid || false,
                    yaxis: {
                        lines: {
                            show: chartOptions.showYAxisLines || false
                        }
                    },
                    xaxis: {
                        lines: {
                            show: chartOptions.showXAxisLines || false
                        }
                    }
                },
                plotOptions: {
                    bar: {
                        horizontal: chartOptions.horizontalBar || false,
                        columnWidth: chartOptions.barWidth || '80%',
                        borderRadius: chartOptions.barBorderRadius || 0,
                        distributed: chartOptions.distributed || false
                    }
                },
                dataLabels: {
                    offsetX: 0,
                    textAnchor: 'start',
                    enabled: chartOptions.dataLabelsOnChart || false,
                    formatter: ( chartOptions.dataLabelsFormatter ) ? chartOptions.dataLabelsFormatter : function ( val ) { return `${val} vendas`; }
                },
                stroke: {
                    show: chartOptions.showStroke || false
                },
                xaxis: {
                    categories: chartData.categories,
                    tickPlacement: 'on',
                    labels: {
                        rotate: chartOptions.labelsRotate || -45,
                        show: chartOptions.showXAxisLabels || false,
                        hideOverlappingLabels: false,
                        minHeight: chartOptions.minHeight || 50,
                        rotateAlways: chartOptions.rotateXAxisLabelsAlways || false
                    },
                    title: {
                        text: chartOptions.titleXAxisText || undefined,
                        style: {
                            cssClass: chartOptions.titleXAxisClass || ''
                        }
                    },
                    axisTicks: {
                        show: chartOptions.showXAxisTicks || false
                    }
                },
                yaxis: {
                    show: chartOptions.showYAxis || false,
                    title: {
                        text: chartOptions.titleYAxis || undefined
                    }
                },
                fill: {
                    opacity: 1
                },
                colors: [ '#537F23', '#F7890B', '#2E6FAE', '#DEC34E', '#ff3100' ],
                legend: {
                    show: chartOptions.showLegends || false,
                    horizontalAlign: 'right',
                    markers: {
                        width: 26,
                        height: 12,
                        radius: 8
                    },
                    onItemClick: {
                        toggleDataSeries: false
                    },
                    onItemHover: {
                        highlightDataSeries: false
                    },
                    labels: {
                        show: chartOptions.showLegends || false
                    }
                },
                theme: {
                    monochrome: {
                        enabled: true,
                        color: '#4D7AEB'
                    }
                },
                title: {
                    text: chartOptions.title,
                    margin: 10,
                    style: {
                        fontSize: '25px',
                        fontWeight: 'bold',
                        color: '#707070'
                    }
                },
                responsive: [
                    {
                        breakpoint: 480,
                        options: {
                            title: {
                                text: chartOptions.title,
                                margin: 10,
                                style: {
                                    fontSize: '17px',
                                    fontWeight: 'bold',
                                    color: '#707070'
                                }
                            }
                        }
                    }
                ]
            }
        };
    }

    return { hasNoData: true };
};

// Função que retorna as labels e valores de cada estado de tipificação.
const totalsByTypification = apiData => {
    const labels = apiData.map( typification => typification.description );
    const series = apiData.map( typification => typification.value );

    return {
        series,
        labels
    };
};

// Função que retorna as categorias e valores do total de tipificações por hora.
const finalTypificationsByHour = apiData => {
    const categories = Object.keys( apiData );
    const values = Object.values( apiData );

    return {
        series: [ {
            name: '',
            data: values
        } ],
        categories
    };
};

export {
    getStatisticsAndLabels,
    addNewPieChart,
    addNewBarChart,
    totalsByTypification,
    finalTypificationsByHour
};